import { Avatar } from "@dash/avatar";
import { SelectDialog } from "@dash/select";
import { useToast } from "@dash/toast";
import { useProfiles } from "@megarax/features";
import { useCallback, useContext, useMemo } from "react";
import { useQueryClient } from "react-query";

import { IamAuthContext } from "@megaron/auth-react";
import { Region } from "@megaron/crm-contracts";
import { UserDoc } from "@megaron/docs-contracts";
import { useClientManager } from "@megaron/react-clients";

import { CustomersButton } from "../CustomersButton";
import { GoToVisitsButton } from "../GoToVisitsButton";
import { RegionOwnerAddress } from "./RegionOwnerAddress";

type Props = {
  profile?: UserDoc;
  legacyUserId?: number;
  region: Region;
  customerCount: number;
  regionQueryKey: string | string[];
};

export const RegionDetails: React.FC<Props> = ({ profile, legacyUserId, region, customerCount, regionQueryKey }) => {
  const toast = useToast();

  const queryClient = useQueryClient();

  const { allProfiles } = useProfiles();

  const auth = useContext(IamAuthContext);

  const isAdmin = useMemo(() => {
    return auth?.iamUser?.roles?.includes("crm.admin");
  }, [auth]);

  const updateRegionMutation = useClientManager("crm").saveRegion().useMutation();

  const handleOwnerChange = useCallback(
    (ownerEmail: string | null) => {
      updateRegionMutation.mutate(
        { ...region, ownerEmail },
        {
          onError: () => {
            toast.error("Nie udało się przypisać handlowca do regionu");
          },
          onSuccess: () => {
            queryClient.invalidateQueries(regionQueryKey);
          },
        },
      );
    },
    [region, updateRegionMutation, toast, queryClient, regionQueryKey],
  );

  const userOptions = allProfiles?.map((profile) => ({
    label: profile.firstName ? `${profile.firstName} ${profile.lastName}` : `${profile.email}`,
    value: profile.email,
  }));

  return (
    <div css={{ display: "flex", flexDirection: "column", gap: "0.75rem", width: "100%" }}>
      <div css={{ display: "flex", alignItems: "center", gap: "1rem", justifyContent: "space-between" }}>
        <SelectDialog
          label={"Handlowiec"}
          options={userOptions ?? []}
          onSelect={(option) => {
            if (option?.value === region.ownerEmail) {
              handleOwnerChange(null);
              return;
            }
            handleOwnerChange(option?.value || null);
          }}
          variant="single-select"
          renderTrigger={(_, __, ___, onOpen) => (
            <button
              onClick={onOpen}
              css={{
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                cursor: "pointer",
                background: "none",
                border: "none",
                padding: 0,
              }}
            >
              <Avatar src={profile ? profile.profilePictureUrl : null} />
              <span css={{ textAlign: "right", fontWeight: 600, fontSize: "1rem" }}>
                {profile?.firstName ? `${profile.firstName} ${profile.lastName}` : profile?.email}
              </span>
            </button>
          )}
          initiallySelectedValues={region.ownerEmail ? [region.ownerEmail] : []}
        />

        {legacyUserId && <GoToVisitsButton legacyUserId={legacyUserId} />}
      </div>
      <CustomersButton regions={[region.name]} customerCount={customerCount} />
      {isAdmin && <RegionOwnerAddress region={region} regionQueryKey={regionQueryKey} />}
    </div>
  );
};
